import React, { useEffect, useState, useContext } from "react";
import { usePlaidLink } from "react-plaid-link";
import { DataContext } from "../../../context/DataContext";
import { Loader, Group } from "@mantine/core";
import useStatusCheck from "../../../hooks/useStatusCheck";
import useEdgeStatusCheck from "../../../hooks/useEdgeStatusCheck";
import { getEdgeQueueUrl, getEdgeRetrieveUrl } from "../../../utils/edge";
import CenterLoadingOverlay from "../../../components/center-loading-overlay";
import * as Sentry from "@sentry/react";
import { startTransaction } from "@sentry/core"; // Import startTransaction from @sentry/core

const Plaid = ({ onClose }) => {
  const [token, setToken] = useState(null);
  const [plaidEvent, setPlaidEvent] = useState(null);
  const [plaidEventMetaData, setPlaidEventMetaData] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [template, setTemplate] = useState([]);
  const [closed, setClosed] = useState(false);
  const [done, setDone] = useState(false);
  const [linkSessionID, setLinkSessionID] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const { settings, data, postMessage } = useContext(DataContext);

  const isEdgeMode = data?.edge === true;
  useStatusCheck(done && !isEdgeMode, false, "idmax");
  useEdgeStatusCheck(done && isEdgeMode);

  // Initialize a Sentry transaction at the beginning of the component

  // Initialize Plaid Link with an empty token which will be replaced once the token is fetched.
  const { open, ready, error } = usePlaidLink({
    token,
    onSuccess: (public_token, metadata) => {
      setLinkSessionID(metadata.link_session_id);
    },
    onExit: (error, metadata) => {
      setClosed(true);
    },
    onEvent: (eventName, metadata) => {
      if (eventName === "IDENTITY_VERIFICATION_CLOSE_UI") {
        setLoaded(false);
        setDone(true);
      }
      setPlaidEvent(eventName);
      setPlaidEventMetaData(metadata);
    },
  });

  // Polling function for edge mode - polls for link_token
  const pollForLinkToken = async (reqId) => {
    const maxAttempts = 100;
    const pollInterval = 3000;
    let attempts = 0;

    const poll = async () => {
      try {
        const response = await fetch(getEdgeRetrieveUrl(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ request_id: reqId }),
        });

        if (response.ok) {
          const result = await response.json();
          console.log("Poll response:", result);

          if (result.link_token) {
            setTemplate(result.template || result);
            setToken(result.link_token);
            setLoaded(true);
            return;
          }
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        } else {
          console.error("Polling timeout: No link_token received after 5 minutes");
        }
      } catch (error) {
        console.error("Polling error:", error);
        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, pollInterval);
        }
      }
    };

    poll();
  };

  useEffect(() => {
    if (!token && !loaded) {
      data.provider = "PLAID";

      if (isEdgeMode) {
        // Edge mode: POST to edge queue, then poll for link_token
        const requestData = { ...data, queue: "idmax" };
        fetch(getEdgeQueueUrl(), {
          method: "POST",
          body: JSON.stringify(requestData),
        })
          .then((response) => response.json())
          .then((data_returned) => {
            const reqId = data_returned.request_id || data.request_id;
            if (reqId) {
              setRequestId(reqId);
              console.log("Starting polling for request_id:", reqId);
              pollForLinkToken(reqId);
            } else {
              console.error("No request_id found in edge response");
            }
          })
          .catch((error) => {
            console.error("Error fetching the link token:", error);
          });
      } else {
        // Normal mode: POST directly to backend
        fetch(
          process.env.REACT_APP_BACKEND_URL + "/v3/stable/api/frontend/idmax",
          {
            method: "POST",
            body: JSON.stringify(data),
          }
        )
          .then((response) => response.json())
          .then((data_returned) => {
            setTemplate(data_returned.continuations.info.template);
            setToken(data_returned.continuations.info.template.info.link_token);
            setLoaded(true);
          })
          .catch((error) => {
            console.error("Error fetching the link token:", error);
          });
      }
    }
  }, [token, loaded, settings.request_id]);

  useEffect(() => {
    if (ready && token) {
      open();
    }
  }, [ready, token, open]);

  useEffect(() => {
    console.log("plaid event", plaidEvent);
    console.log("plaid event", JSON.stringify(plaidEventMetaData));
    console.log("plaid error", error);
    console.log("plaid closed", closed);
  }, [plaidEvent, plaidEventMetaData, error, closed]);

  useEffect(() => {
    if (closed) {
      postMessage("CLOSE", { trigger: "plaid_closed" });
    }
  }, [closed]);

  useEffect(() => {
    if (linkSessionID) {
      fetchStatus();
    }
  }, [linkSessionID]);

  const fetchStatus = async () => {
    if (isEdgeMode) {
      // Edge mode: POST to edge retrieve with completion data
      try {
        const response = await fetch(getEdgeRetrieveUrl(), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            request_id: requestId || settings.request_id,
            type: "idmax",
            code: linkSessionID,
            identity_verification_id: linkSessionID,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data_returned = await response.json();
      } catch (error) {
        console.error("Error fetching status:", error);
      }
      return;
    }

    // Normal mode
    data.isIDMaxContinue = true;
    data.template = template;
    data.template.info.identity_verification_id = linkSessionID;

    try {
      const response = await fetch(
        process.env.REACT_APP_BACKEND_URL + "/v3/stable/api/frontend/idmax",
        {
          method: "POST",
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data_returned = await response.json();
    } catch (error) {
      console.error("Error fetching status:", error);
    }
  };

  // Render a loading indicator if we are waiting for the token
  if (!loaded) {
    return (
      <CenterLoadingOverlay
        content={
          <>
            <Group position="center">
              <Loader />
            </Group>
          </>
        }
      />
    );
  }

  return null;
};

// Sentry is currently disabled
// export default Sentry.withProfiler(Plaid);
export default Plaid;
